import React, { useState, useEffect, useRef } from 'react';
import { fetchWithAuth } from '../../../../utils/api';
import { FiPlus, FiX } from 'react-icons/fi';
import IconWrapper from '../../../IconWrapper';

interface AddLabelAutocompleteProps {
  dimension: string;
  existingLabels: string[];
  onAddLabel: (label: { dimension: string; category: string; label: string }) => void;
  color: string;
}

interface TaxonomyLabel {
  label_key: string;
  label_name: string;
  category: string;
  category_name: string;
  description: string;
}

const AddLabelAutocomplete: React.FC<AddLabelAutocompleteProps> = ({
  dimension,
  existingLabels,
  onAddLabel,
  color
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [availableLabels, setAvailableLabels] = useState<TaxonomyLabel[]>([]);
  const [filteredLabels, setFilteredLabels] = useState<TaxonomyLabel[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fetch available labels for this dimension
  useEffect(() => {
    const fetchLabels = async () => {
      setIsLoading(true);
      try {
        const endpoint = `/v1/taxonomy/labels?dimension=${dimension}`;
        console.log('🔍 [AddLabelAutocomplete] Fetching taxonomy labels from:', endpoint);
        console.log('🔍 [AddLabelAutocomplete] Dimension:', dimension);
        console.log('🔍 [AddLabelAutocomplete] Existing labels to filter out:', existingLabels);
        
        const response = await fetchWithAuth(endpoint);
        console.log('📦 [AddLabelAutocomplete] Raw API response:', response);
        console.log('📦 [AddLabelAutocomplete] Response type:', typeof response);
        console.log('📦 [AddLabelAutocomplete] Response keys:', response ? Object.keys(response) : 'null response');
        
        // Log the full URL being called
        console.log('🔗 [AddLabelAutocomplete] Full API URL:', `${endpoint}`);
        
        // Check if we have the expected structure
        if (!response) {
          console.error('❌ [AddLabelAutocomplete] No response received from API');
          setAvailableLabels([]);
          return;
        }
        
        if (!response.dimensions) {
          console.error('❌ [AddLabelAutocomplete] Response missing "dimensions" property. Response structure:', response);
          setAvailableLabels([]);
          return;
        }
        
        const dimensionData = response.dimensions[dimension];
        console.log('🎯 [AddLabelAutocomplete] Dimension data for', dimension, ':', dimensionData);
        
        if (dimensionData && dimensionData.labels && Array.isArray(dimensionData.labels)) {
          console.log('✅ [AddLabelAutocomplete] Found', dimensionData.labels.length, 'labels for dimension');
          
          // Filter out labels that already exist
          const labels = dimensionData.labels.filter(
            (label: TaxonomyLabel) => !existingLabels.includes(label.label_name)
          );
          console.log('🔧 [AddLabelAutocomplete] Available labels after filtering:', labels.length, 'remaining');
          console.log('🔧 [AddLabelAutocomplete] First few labels:', labels.slice(0, 3));
          
          setAvailableLabels(labels);
        } else {
          console.warn('⚠️ [AddLabelAutocomplete] No labels found for dimension:', dimension);
          console.warn('⚠️ [AddLabelAutocomplete] dimensionData:', dimensionData);
          setAvailableLabels([]);
        }
      } catch (error) {
        console.error('❌ [AddLabelAutocomplete] Failed to fetch taxonomy labels:', error);
        console.error('❌ [AddLabelAutocomplete] Error details:', {
          message: (error as any)?.message,
          stack: (error as any)?.stack
        });
        setAvailableLabels([]);
      } finally {
        setIsLoading(false);
      }
    };

    if (isOpen) {
      console.log('🚀 [AddLabelAutocomplete] Component opened, fetching labels...');
      fetchLabels();
    }
  }, [dimension, isOpen, existingLabels]);

  // Filter labels based on search term
  useEffect(() => {
    console.log('🔄 [AddLabelAutocomplete] Filtering labels. Search term:', searchTerm);
    console.log('🔄 [AddLabelAutocomplete] Available labels count:', availableLabels.length);
    
    if (searchTerm) {
      const filtered = availableLabels.filter(label =>
        label.label_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        label.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
      console.log('🔄 [AddLabelAutocomplete] Filtered labels count:', filtered.length);
      setFilteredLabels(filtered);
      setSelectedIndex(0);
    } else {
      console.log('🔄 [AddLabelAutocomplete] No search term, using all available labels');
      setFilteredLabels(availableLabels);
      setSelectedIndex(0);
    }
  }, [searchTerm, availableLabels]);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, filteredLabels.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredLabels[selectedIndex]) {
        handleSelectLabel(filteredLabels[selectedIndex]);
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchTerm('');
    }
  };

  const handleSelectLabel = (label: TaxonomyLabel) => {
    onAddLabel({
      dimension: dimension,
      category: label.category,
      label: label.label_name
    });
    setIsOpen(false);
    setSearchTerm('');
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!isOpen) {
    return (
      <button
        onClick={() => {
          setIsOpen(true);
          setTimeout(() => inputRef.current?.focus(), 0);
        }}
        className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 border border-dashed border-gray-300 rounded-full hover:border-gray-400 transition-all"
      >
        <IconWrapper icon={FiPlus} className="h-4 w-4" />
        Add missing label...
      </button>
    );
  }

  // Log before the return statement
  console.log('🎨 [AddLabelAutocomplete] Rendering dropdown. isLoading:', isLoading, 'filteredLabels.length:', filteredLabels.length);

  return (
    <div className="relative" ref={dropdownRef}>
      <div className="flex items-center gap-2">
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Search for missing labels..."
          className="px-3 py-1.5 text-sm border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-64"
        />
        <button
          onClick={() => {
            setIsOpen(false);
            setSearchTerm('');
          }}
          className="p-1.5 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <IconWrapper icon={FiX} className="h-4 w-4" />
        </button>
      </div>

      {/* Dropdown */}
      {(isLoading || filteredLabels.length > 0) && (
        <div className="absolute z-10 mt-2 w-80 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {isLoading ? (
            <div className="px-4 py-3 text-sm text-gray-500">Loading labels...</div>
          ) : filteredLabels.length > 0 ? (
            <ul className="py-1">
              {filteredLabels.map((label, index) => (
                <li
                  key={label.label_key}
                  className={`px-4 py-2 cursor-pointer transition-colors ${
                    index === selectedIndex
                      ? 'bg-blue-50 text-blue-900'
                      : 'hover:bg-gray-50 text-gray-700'
                  }`}
                  onClick={() => handleSelectLabel(label)}
                  onMouseEnter={() => setSelectedIndex(index)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="font-medium text-sm">{label.label_name}</div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        {label.category_name}
                      </div>
                      {label.description && (
                        <div className="text-xs text-gray-600 mt-1 line-clamp-2">
                          {label.description}
                        </div>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          ) : searchTerm ? (
            <div className="px-4 py-3 text-sm text-gray-500">
              No labels found matching "{searchTerm}"
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default AddLabelAutocomplete;